/**
 * The pure half of the ETF tabs (`components/EtfViews.tsx`): the tiles, the
 * treemap's tiles with the small lines folded away, and the sorts.
 * `docs/plans/etf-components.md`; the approved design is
 * `docs/mockups/etf-components.html`.
 */
import type { EtfHolder, EtfLine } from "../api/etf";

/**
 * The map draws this many of the largest lines; the rest fold into one "Other"
 * tile. A count, not a weight: a 0.5% cut-off (the 13F map's) folded 44.7% of
 * SPY and all of RSP, an equal-weight fund whose lines are 0.2% each (owner,
 * 2026-10-30). 500 covers an S&P 500 fund; a total-market fund folds its tail.
 */
export const TREEMAP_MAX_TILES = 500;

export interface ComponentTiles {
  /** Every line the fund publishes, charted or not. */
  holdings: number;
  /** Sum of the ten largest weights. */
  top10: number;
  up: number;
  down: number;
  /** The day's move weighted by each line's weight, over the lines that have one. */
  weightedChange: number | null;
}

export function componentTiles(lines: readonly EtfLine[]): ComponentTiles {
  const weights = lines.map((l) => l.weight ?? 0).sort((a, b) => b - a);
  let num = 0, den = 0, up = 0, down = 0;
  for (const l of lines) {
    const c = l.changePercentCc;
    if (c == null) continue;
    if (c > 0) up++;
    else if (c < 0) down++;
    if (l.weight != null) {
      num += c * l.weight;
      den += l.weight;
    }
  }
  return {
    holdings: lines.length,
    top10: weights.slice(0, 10).reduce((s, w) => s + w, 0),
    up,
    down,
    weightedChange: den > 0 ? num / den : null,
  };
}

export interface MapTile {
  /** The ticker; null for the Other tile and for a line that is not a ticker. */
  symbol: string | null;
  label: string;
  v: number;
  change: number | null;
  sector: string | null;
  /** Whether a click can chart it. */
  clickable: boolean;
  other: boolean;
}

/**
 * The tiles, heaviest first: the largest `max` lines, then one "Other" tile for
 * what is left — the lines past them plus whatever the published weights fall
 * short of 100. A line with no weight cannot be drawn and is left to the table.
 */
export function mapTiles(lines: readonly EtfLine[], max = TREEMAP_MAX_TILES): MapTile[] {
  const tiles: MapTile[] = [];
  let other = 0;
  let total = 0;
  const weighted = lines.filter((l) => l.weight != null && l.weight > 0).sort((a, b) => b.weight! - a.weight!);
  for (const [i, l] of weighted.entries()) {
    const w = l.weight!;
    total += w;
    if (i >= max) {
      other += w;
      continue;
    }
    tiles.push({
      symbol: l.symbol, label: l.symbol ?? l.name ?? "—", v: w, change: l.changePercentCc,
      sector: l.sector, clickable: l.inUniverse && l.symbol != null, other: false,
    });
  }
  other += Math.max(0, 100 - total);
  if (other > 0.05) {
    tiles.push({ symbol: null, label: "Other", v: other, change: null, sector: null, clickable: false, other: true });
  }
  return tiles.sort((a, b) => b.v - a.v);
}

export type ComponentKey = "weight" | "symbol" | "close" | "change" | "sector";
export interface Sort<K> { key: K; dir: 1 | -1 }

/** Text sorts A→Z first, numbers largest first. */
export function firstDir(key: ComponentKey): 1 | -1 {
  return key === "symbol" || key === "sector" ? 1 : -1;
}

function value(l: EtfLine, key: ComponentKey): number | string | null {
  switch (key) {
    case "weight": return l.weight;
    case "symbol": return l.symbol ?? l.name;
    case "close": return l.close;
    case "change": return l.changePercentCc;
    case "sector": return l.sector;
  }
}

/** Sorted by `sort`; a missing value goes last whichever way the sort runs. */
export function sortLines(lines: readonly EtfLine[], sort: Sort<ComponentKey>): EtfLine[] {
  return [...lines].sort((a, b) => {
    const x = value(a, sort.key), y = value(b, sort.key);
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return (x < y ? -1 : x > y ? 1 : 0) * sort.dir;
  });
}

export type HolderOrder = "weight" | "value";

/**
 * Funds by weight in the fund (who is concentrated in it) or by $ held (who
 * owns the most). By value leaves out a fund with no value: it has nothing to
 * rank on.
 */
export function orderHolders(funds: readonly EtfHolder[], by: HolderOrder): EtfHolder[] {
  if (by === "value") return funds.filter((f) => f.value != null).sort((a, b) => b.value! - a.value!);
  return [...funds].sort((a, b) => (b.weight ?? -1) - (a.weight ?? -1));
}

/** `2× long`, `3× short`; a name that gave no number reads `lev. long`. */
export function leverageLabel(multiple: number | null, direction: "long" | "short"): string {
  return `${multiple == null ? "lev." : `${multiple}×`} ${direction}`;
}

/** `$6.4B`, `$812M`, `$45K`. */
export function money(v: number | null): string {
  if (v == null) return "—";
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${Math.round(v / 1e6)}M`;
  if (v >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${Math.round(v)}`;
}

/** `+1.42%`, `-0.61%`, `—`. */
export function signedPct(v: number | null, digits = 2): string {
  if (v == null) return "—";
  return `${v >= 0 ? "+" : ""}${v.toFixed(digits)}%`;
}
